"use client";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export type FeatureVariant = "default" | "emerald" | "amber" | "purple" | "blue";

export interface FeatureShowcaseProps {
  /** Color variant for the feature */
  variant?: FeatureVariant;
  /** Badge text */
  badge?: string;
  /** Icon component */
  icon: React.ComponentType<{ className?: string }>;
  /** Feature title */
  title: string;
  /** Feature description */
  description: string;
  /** Whether this feature is highlighted/featured */
  featured?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Footer content (buttons, links, previews) */
  children?: React.ReactNode;
}

const variantStyles: Record<FeatureVariant, {
  iconBg: string;
  iconColor: string;
  border: string;
  badgeBg: string;
  badgeText: string;
}> = {
  default: {
    iconBg: "bg-slate-500/10",
    iconColor: "text-slate-400",
    border: "border-border/50",
    badgeBg: "",
    badgeText: "",
  },
  emerald: {
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-500",
    border: "border-emerald-500/30",
    badgeBg: "bg-emerald-500/20",
    badgeText: "text-emerald-400 border-emerald-500/30",
  },
  amber: {
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-500",
    border: "border-amber-500/30",
    badgeBg: "bg-amber-500/20",
    badgeText: "text-amber-400 border-amber-500/30",
  },
  purple: {
    iconBg: "bg-purple-500/10",
    iconColor: "text-purple-500",
    border: "border-purple-500/30",
    badgeBg: "bg-purple-500/20",
    badgeText: "text-purple-400 border-purple-500/30",
  },
  blue: {
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-500",
    border: "border-blue-500/30",
    badgeBg: "bg-blue-500/20",
    badgeText: "text-blue-400 border-blue-500/30",
  },
};

/**
 * FeatureShowcase - Card for displaying product features
 *
 * Use for feature sections on landing pages with icon, title, description pattern.
 *
 * @example
 * <FeatureShowcase
 *   variant="emerald"
 *   badge="Member"
 *   icon={Trophy}
 *   title="Challenge & Connect"
 *   description="Send challenges to other golfers..."
 *   featured
 * >
 *   <Button>Get Started</Button>
 * </FeatureShowcase>
 */
export function FeatureShowcase({
  variant = "default",
  badge,
  icon: Icon,
  title,
  description,
  featured = false,
  className,
  children,
}: FeatureShowcaseProps) {
  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        "flex flex-col items-center text-center p-4 sm:p-6 rounded-lg shadow-lg bg-background",
        "hover:shadow-xl transition-shadow",
        featured ? `border-2 ${styles.border}` : "border border-border/50",
        className
      )}
    >
      {/* Badge */}
      {badge && (
        <div className="flex items-center gap-2 mb-4">
          <Badge
            variant="outline"
            className={cn(
              "text-xs",
              variant !== "default" && styles.badgeBg,
              variant !== "default" && styles.badgeText
            )}
          >
            {badge}
          </Badge>
        </div>
      )}

      {/* Icon */}
      <div
        className={cn(
          "rounded-full p-4 mb-4",
          styles.iconBg,
          styles.iconColor
        )}
      >
        <Icon className="h-10 w-10" />
      </div>

      {/* Title */}
      <h3 className="text-2xl font-bold mb-3">{title}</h3>

      {/* Description */}
      <p className="text-muted-foreground mb-4">{description}</p>

      {/* Footer Content */}
      {children && <div className="w-full mt-auto space-y-3">{children}</div>}
    </div>
  );
}

export default FeatureShowcase;

